import { MouseEvent, ReactNode, useEffect, useRef } from 'react';
import clsx from 'clsx';
import { useVirtualizer, type VirtualItem } from '@tanstack/react-virtual';
import type { HeaderFilterValue } from './useHeaderFilterPages';

/** С какого размера список виртуализуется: короткий дешевле отрисовать целиком. */
const VIRTUAL_FROM = 60;
/** Высота строки значения до измерения (`.dtb-menu__check`). */
const ROW_HEIGHT = 34;
/** За сколько пикселей до конца прокрутки просить следующую порцию. */
const NEAR_END = 120;
/** То же для виртуального списка — в строках. */
const NEAR_END_ROWS = 4;

interface IHeaderFilterListProps {
    values: HeaderFilterValue[];
    selected: ReadonlySet<string | null>;
    onValueClick: (index: number, key: string | null) => (e: MouseEvent<HTMLInputElement>) => void;
    onToggle: (key: string | null) => void;
    /** Список дочитан почти до конца: порционная воронка грузит следующую порцию. */
    onNearEnd?: () => void;
    /** Над значениями: «Загрузка…», отказ, «Нет значений». */
    before?: ReactNode;
    /** Под значениями: ход и отказ следующей порции. */
    after?: ReactNode;
}

/** Значения воронки: длинный список виртуализован, в DOM — только видимые строки и запас. */
export function DataTableHeaderFilterList({
    values,
    selected,
    onValueClick,
    onToggle,
    onNearEnd,
    before,
    after,
}: IHeaderFilterListProps) {
    const scrollRef = useRef<HTMLDivElement>(null);
    const virtual = values.length >= VIRTUAL_FROM;
    const virtualizer = useVirtualizer({
        useFlushSync: false,
        enabled: virtual,
        count: values.length,
        getScrollElement: () => scrollRef.current,
        estimateSize: () => ROW_HEIGHT,
        getItemKey: (i) => values[i].key ?? ' ',
        overscan: 6,
    });

    // Конец списка: у виртуального — последняя отрисованная строка у края загруженного (высота прокрутки в кадр
    // включения виртуализации ещё не та), у короткого — прокрутка у дна. Короткий ответ или поиск по загруженному
    // просят следующую порцию сразу.
    const items = virtual ? virtualizer.getVirtualItems() : [];
    const lastRendered = items.at(-1)?.index ?? -1;
    const checkNearEnd = () => {
        if (virtual) {
            if (lastRendered >= values.length - NEAR_END_ROWS) onNearEnd?.();
            return;
        }
        const el = scrollRef.current;
        if (el && el.scrollHeight - el.scrollTop - el.clientHeight < NEAR_END) onNearEnd?.();
    };
    useEffect(checkNearEnd, [virtual, lastRendered, values.length, onNearEnd]);

    const renderValue = (f: HeaderFilterValue, index: number, item?: VirtualItem) => (
        <label
            key={f.key ?? ' '}
            ref={item ? virtualizer.measureElement : undefined}
            data-index={item?.index}
            style={item ? { transform: `translateY(${item.start}px)` } : undefined}
            className={clsx('dtb-menu__check', f.key == null && 'dtb-menu__check--empty', item && 'dtb-hflist__row')}
        >
            <input
                type="checkbox"
                checked={selected.has(f.key)}
                onClick={onValueClick(index, f.key)}
                onChange={() => onToggle(f.key)}
            />
            <span className="dtb-hfvalue">{f.label}</span>
            {f.count != null && <span className="dtb-hfcount">{f.count}</span>}
        </label>
    );

    return (
        <div ref={scrollRef} className="dtb-menu__scroll" onScroll={onNearEnd ? checkNearEnd : undefined}>
            {before}
            {virtual ? (
                <div className="dtb-hflist" style={{ height: virtualizer.getTotalSize() }}>
                    {items.map((item) => renderValue(values[item.index], item.index, item))}
                </div>
            ) : (
                values.map((f, index) => renderValue(f, index))
            )}
            {after}
        </div>
    );
}
